import { FC, ReactNode, useState } from 'react';

import { event } from 'cypress/types/jquery';

import { useAnalytics } from '@hooks/useAnalytics';
import { useShareLink } from '@hooks/useShareLink';

import Button from '@components/buttons/Button';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';

const useCopyText = () => {
  const [didCopy, setDidCopy] = useState(false);

  const copyText = async ({ text }: { text: string }) => {
    await navigator.clipboard.writeText(text);
    setDidCopy(true);

    setTimeout(() => {
      setDidCopy(false);
    }, 3000);
  };

  return { didCopy, copyText };
};

export const CopyText: FC<{
  startIcon?: ReactNode;
  text: string;
  displayText?: string;
  clickAction?: 'copy' | 'share';
  track?: {
    event: string;
    properties?: Record<string, any>;
  };
  buttonText?: {
    copy?: string;
    copied?: string;
  };
}> = ({
  startIcon,
  text,
  displayText,
  clickAction = 'copy',
  track: trackProperties,
  buttonText,
}) => {
  const { track } = useAnalytics();
  const { didCopy: didCopyText, copyText } = useCopyText();
  const { didCopy: didShare, shareLink } = useShareLink();
  const { copy = 'Copy', copied = 'Copied!' } = buttonText ?? {};
  const didCopy = clickAction === 'copy' ? didCopyText : didShare;

  const handleClick = () => {
    if (trackProperties) {
      track(trackProperties.event, trackProperties.properties);
    }

    if (clickAction === 'copy') {
      copyText({ text });
    } else {
      shareLink({ url: text });
    }
  };

  return (
    <div
      className="d-flex align-items-center justify-content-between ps-3"
      style={{
        border: '1px solid rgba(255,255,255,.5)',
        borderRadius: '.5rem',
        background: 'rgba(255,255,255,.1)',
      }}
    >
      <div className="d-flex align-items-center gap-2" style={{ overflow: 'hidden' }}>
        {startIcon && <div>{startIcon}</div>}
        <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
          <span>{displayText ?? text}</span>
        </div>
      </div>
      <div>
        <Button
          startIcon={didCopy ? <FontAwesomeIcon name="check" /> : <FontAwesomeIcon name="share" />}
          roundness="rounded"
          variant="flat"
          color="neutral"
          size="sm"
          onClick={handleClick}
        >
          {didCopy ? copied : copy}
        </Button>
      </div>
    </div>
  );
};
